import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import * as ReactRedux from 'react-redux';
import * as Actions from 'actions';
import withSelfServeDragAndResize from 'withSelfServeDragAndResize';
import { enablePopupCreation } from 'SelfServeBuilder/SelfServeBuilderActions';
import { removeFieldFromSectionOrganizer } from 'SelfServePostBuilderSidePanel/SectionOrganizer/SectionOrganizerActions';
import { removeFieldFromGroup, setActiveGroup } from 'PopupGroup/PopupGroupActions';
import { setActiveToolboxToolItem } from 'SelfServeBuilderToolbox/SelfServeBuilderToolboxActions';
import ImageButton from 'ImageButton';

const withSelfServeCreatorGroup = (WrappedComponent, withSelfServeCreatorProps = {}) => {
  class WrappedComponentWithSelfServeCreatorGroup extends React.PureComponent {
    constructor(props) {
      super(props);

      this.isActiveGroup = this.isActiveGroup.bind(this);
      this.onDelete = this.onDelete.bind(this);
      this.setActiveGroup = this.setActiveGroup.bind(this);
    }

    onDelete() {
      const { id, activeGroupId } = this.props;
      this.props.removeFieldFromGroup(activeGroupId, id);
      const newIds = this.props.popupGroup.fieldIds.filter(
        fieldId => fieldId !== id,
      );
      const latestIndex = Math.max(newIds.length - 1, 0);
      const newActiveId = newIds[latestIndex] || null;
      if (!newActiveId) {
        this.props.removeFieldFromSectionOrganizer(activeGroupId);
        this.props.addPaddingTop(this.props.id, 0);
      }
      this.props.onDeletePopup(id, newActiveId);
      this.props.enablePopupCreation();
    }

    setActiveGroup() {
      const { groupId, popupGroup, activeGroupId, isEditingGroup, activePopup } = this.props;
      const { type, fieldIds } = popupGroup;
      // If there is an activePopup and it is not part of this group, return
      if (activePopup && !fieldIds.includes(activePopup)) return;
      if (isEditingGroup) return;
      if (!activeGroupId || (activeGroupId && activeGroupId !== groupId)) {
        if (this.props.activeToolboxItem !== type) {
          this.props.setActiveToolboxToolItem(type);
        }
        this.props.setActiveGroup(groupId);
      }
    }

    isActiveGroup() {
      if (this.props.isPreviewMode) return false;
      if (!this.props.activeGroupId) {
        return false;
      }
      if (this.props.groupId === this.props.activeGroupId) {
        const fieldIds = this.props.popupGroup.fieldIds;
        return fieldIds.includes(this.props.id);
      }
      return false;
    }

    render() {
      const { isPreviewMode, id } = this.props;
      const isActiveGroup = this.isActiveGroup();
      let zoomFactor = this.props.zoomFactor;
      if (!isPreviewMode) {
        zoomFactor = 1;
      }
      return (
        <div
          id={isPreviewMode ? `preview-${id}` : id}
          className="self-serve-creator-popup"
          onClick={this.setActiveGroup}
        >
          {isActiveGroup &&
            <ImageButton
              className="delete-img-btn"
              noMargin={false}
              onClick={this.onDelete}
              alt="Delete"
              src="/images/self-serve-builder/delete-box-2x.png"
              srcHover="/images/self-serve-builder/delete-box-hover-2x.png"
            />
          }
          <div className="drag-handle">
            <WrappedComponent
              isActiveGroup={isActiveGroup}
              isActivePopup={this.props.activePopup === this.props.id && this.props.isEditingGroup}
              height={this.props.height}
              width={this.props.width}
              zoomFactor={zoomFactor}
              popupId={this.props.id}
              groupData={this.props.groupData}
              isPreviewMode={this.props.isPreviewMode}
            />
          </div>
          {!isPreviewMode &&
            <React.Fragment>
              {this.props.resizeHandleComponent}
            </React.Fragment>
          }
        </div>
      );
    }
  }

  WrappedComponentWithSelfServeCreatorGroup.defaultProps = {
    activeGroupId: '',
    activePopup: '',
    activeToolboxItem: '',
    resizeHandleComponent: null,
    groupData: '',
  };
  WrappedComponentWithSelfServeCreatorGroup.propTypes = {
    activeGroupId: PropTypes.string,
    activePopup: PropTypes.string,
    activeToolboxItem: PropTypes.string,
    addPaddingTop: PropTypes.func.isRequired,
    enablePopupCreation: PropTypes.func.isRequired,
    groupId: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    isPreviewMode: PropTypes.bool.isRequired,
    isEditingGroup: PropTypes.bool.isRequired,
    onDeletePopup: PropTypes.func.isRequired,
    popupGroup: PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      pageIndex: PropTypes.number,
      fieldIds: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    removeFieldFromSectionOrganizer: PropTypes.func.isRequired,
    removeFieldFromGroup: PropTypes.func.isRequired,
    resizeHandleComponent: PropTypes.node,
    setActiveGroup: PropTypes.func.isRequired,
    setActiveToolboxToolItem: PropTypes.func.isRequired,
    groupData: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
    ]),
    width: PropTypes.number.isRequired,
    zoomFactor: PropTypes.number.isRequired,
  };

  const mapStateToProps = ({
    popupHandler,
    SelfServeBuilderReducer,
    PopupGroupReducer,
    SelfServeBuilderToolboxReducer,
    StaticBuilderReducer,
  }, ownProps) => ({
    activePopup: popupHandler.activePopup,
    activeGroupId: PopupGroupReducer.activeGroupId,
    activeToolboxItem: SelfServeBuilderToolboxReducer.activeToolboxItem,
    height: ownProps.data.height,
    isEditingGroup: PopupGroupReducer.isEditingGroup,
    groupData: StaticBuilderReducer.fieldData[ownProps.groupId] || null,
    width: ownProps.data.width,
    x: ownProps.data.x,
    y: ownProps.data.y,
    zIndex: ownProps.data.zIndex,
  });

  const mapDispatchToProps = dispatch => (
    bindActionCreators({
      onMove: Actions.movePopup,
      onAttributesChanged: Actions.changePopup,
      onDeletePopup: Actions.deleteGroupPopup,
      enablePopupCreation,
      removeFieldFromGroup,
      setActiveGroup,
      setActiveToolboxToolItem,
      removeFieldFromSectionOrganizer,
    }, dispatch)
  );

  return ReactRedux.connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withSelfServeDragAndResize(WrappedComponentWithSelfServeCreatorGroup,
    withSelfServeCreatorProps.withDragAndResizeProps || {},
  ));
};

export default withSelfServeCreatorGroup;



// WEBPACK FOOTER //
// ./src-react/withSelfServeCreator/withSelfServeCreatorGroup.jsx